import { useState } from "react";
import { Box, Button, Menu, MenuItem, ButtonGroup } from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import { Run } from "../../../services/appsync/types";
import { useMemo } from "react";

export type StartRunDropdownOption = {
  label: string;
  onClick: () => void;
  // Optional second line under the label (11px, muted) — used by the Full
  // Pull menu to state what each action pulls.
  caption?: string;
  disabled?: boolean;
};

export const StartRunDropdownComponent = ({
  engineInProgress,
  runInProgress,
  options,
  onRunWithLockedForecast,
  onRunWithRecalculatedForecast,
  loading,
}: {
  engineInProgress: boolean;
  runInProgress: Run | null;
  options?: StartRunDropdownOption[];
  onRunWithLockedForecast?: () => void;
  onRunWithRecalculatedForecast?: () => void;
  loading: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const resolvedOptions: StartRunDropdownOption[] = useMemo(() => {
    if (options && options.length > 0) {
      return options;
    }
    const fallback: StartRunDropdownOption[] = [];
    if (onRunWithLockedForecast) {
      fallback.push({
        label: "Run with locked forecast",
        onClick: onRunWithLockedForecast,
      });
    }
    if (onRunWithRecalculatedForecast) {
      fallback.push({
        label: "Run with recalculated forecast",
        onClick: onRunWithRecalculatedForecast,
      });
    }
    return fallback;
  }, [options, onRunWithLockedForecast, onRunWithRecalculatedForecast]);

  const buttonLabel = useMemo(() => {
    if (engineInProgress && runInProgress) {
      if (runInProgress?.runSettings.workflowId === "workflow_atomic_pivots") {
        return `Running ... ${runInProgress?.atStep! / runInProgress?.totalSteps! ? `${Math.round((runInProgress?.atStep! / runInProgress?.totalSteps!) * 100)}%` : ""}`;
      } else if (
        runInProgress?.runSettings.workflowId === "google_drive_sync"
      ) {
        return `Data sync ...`;
      }
    }
    return "Start Run";
  }, [engineInProgress, runInProgress]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (engineInProgress || loading) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionClick = (option: StartRunDropdownOption) => () => {
    option.onClick();
    handleClose();
  };

  return (
    <>
      <ButtonGroup
        variant={engineInProgress ? "outlined" : "contained"}
        // Match StartRunButtonComponent exactly (34px control, 12.5px/600,
        // 6px radius, brandBlue) so the grouped and plain variants are
        // indistinguishable next to the header's other 34px controls.
        sx={{
          mr: 1,
          height: 34,
          borderRadius: "6px",
          "& .MuiButtonGroup-grouped": {
            height: 34,
            fontSize: "12.5px",
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: (t) =>
              engineInProgress ? undefined : t.palette.atomic.brandBlue,
            color: engineInProgress ? undefined : "#fff",
            "&:hover": {
              backgroundColor: (t) =>
                engineInProgress ? undefined : t.palette.atomic.brandBlueDark,
            },
          },
          "& .MuiButtonGroup-grouped:first-of-type": {
            borderRadius: "6px 0 0 6px",
            padding: "0 14px",
          },
          "& .MuiButtonGroup-grouped:last-of-type": {
            borderRadius: "0 6px 6px 0",
          },
          "& .MuiButtonGroup-grouped:not(:last-of-type)": {
            borderRightColor: (t) =>
              engineInProgress ? undefined : t.palette.atomic.brandBlueDark,
          },
        }}
      >
        <Button onClick={handleClick} disabled={engineInProgress || loading}>
          {buttonLabel}
        </Button>
        <Button
          onClick={handleClick}
          disabled={engineInProgress || loading}
          sx={{ minWidth: "auto", px: 1 }}
        >
          <ArrowDropDown />
        </Button>
      </ButtonGroup>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {resolvedOptions.map((option) => (
          <MenuItem
            key={option.label}
            onClick={handleOptionClick(option)}
            disabled={option.disabled}
          >
            {option.caption ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                <Box sx={{ fontSize: "12.5px", fontWeight: 600 }}>
                  {option.label}
                </Box>
                <Box sx={{ fontSize: "11px", color: "text.secondary" }}>
                  {option.caption}
                </Box>
              </Box>
            ) : (
              option.label
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
